import type { DataProvider } from '@refinedev/core';
import { API_BASE_URL, API_PREFIX, apiUrl } from './config';
import { rawRequest, rawUnversionedRequest } from './client';

/**
 * Refine data provider for the admin API (03 §12).
 *
 * Two shapes come back from `GET`: master data and the wizard list return a
 * bare array, analytics returns `{ data, meta }` with a cursor. Both are
 * normalised to Refine's `{ data, total }` here so no screen has to know which
 * it is dealing with.
 *
 * ⚠️ `total` is the length of the page, not a count of everything. The API is
 * cursor-paginated on purpose (CLAUDE.md §5) and exposes no total anywhere, so
 * any UI that renders "page 3 of 12" would be inventing the 12. The cursor is
 * carried on `meta.nextCursor` and consumed by `useCursorPagination`.
 */

type Json = Record<string, unknown>;

interface CursorEnvelope {
  data: unknown[];
  meta?: { nextCursor: string | null; hasMore: boolean };
}

function isCursorEnvelope(body: unknown): body is CursorEnvelope {
  return typeof body === 'object' && body !== null && Array.isArray((body as CursorEnvelope).data);
}

async function readJson(response: Response): Promise<unknown> {
  // 204 is the only status that legitimately carries no body.
  if (response.status === 204) return undefined;

  const text = await response.text();

  // ⚠️ Throws rather than returning undefined, and that distinction cost an
  // afternoon. A `304 Not Modified` has no body; returning `undefined` made the
  // query SUCCEED with no data, so the wizard editor sat on loading skeletons
  // forever while the API was answering correctly. A silent undefined turns a
  // caching problem into "the page is broken" with nothing to go on.
  //
  // The API no longer emits automatic ETags (see `configureApp`), so this
  // should be unreachable — which is exactly when it is worth keeping loud.
  if (!text) {
    throw new Error(
      `${response.status} from ${response.url} with an empty body. A conditional ` +
        'response (304) cannot be rendered — the request needs to be unconditional.',
    );
  }

  // ⚠️ Checked BEFORE parsing, because `JSON.parse` on an HTML document says
  // "Unexpected token '<'", which tells you nothing about the cause. In dev
  // that cause is almost always a path Vite does not proxy: the SPA fallback
  // answers `index.html` with status **200**, so the request looks successful
  // and fails deep inside a component. `/health/*` did exactly this.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    throw new Error(
      `${response.url} returned ${contentType || 'no content type'} instead of JSON. ` +
        'In development this usually means the path is not in the Vite dev proxy ' +
        '(see apps/admin/vite.config.ts) and the SPA fallback served index.html.',
    );
  }

  return JSON.parse(text) as unknown;
}

function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    // Only primitives go on a query string. Anything else would serialise to
    // "[object Object]" and produce a request that fails validation for a
    // reason nothing in the response explains.
    if (typeof value === 'string') search.set(key, value);
    else if (typeof value === 'number' || typeof value === 'boolean') {
      search.set(key, value.toString());
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const dataProvider: DataProvider = {
  getApiUrl: () => `${API_BASE_URL}${API_PREFIX}`,

  async getList({ resource, meta }) {
    const response = await rawRequest(`/${resource}${buildQuery(meta?.query as Json | undefined)}`);
    const body = await readJson(response);

    if (Array.isArray(body)) {
      return { data: body as never[], total: body.length };
    }
    if (isCursorEnvelope(body)) {
      return {
        data: body.data as never[],
        total: body.data.length,
        // Surfaced to callers through `useList`'s `query.data`; Refine passes
        // unknown keys through untouched.
        ...{ nextCursor: body.meta?.nextCursor ?? null, hasMore: body.meta?.hasMore ?? false },
      };
    }
    return { data: [], total: 0 };
  },

  async getOne({ resource, id }) {
    const response = await rawRequest(`/${resource}/${String(id)}`);
    return { data: (await readJson(response)) as never };
  },

  async create({ resource, variables }) {
    const response = await rawRequest(`/${resource}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    });
    return { data: (await readJson(response)) as never };
  },

  async update({ resource, id, variables, meta }) {
    // Master data updates are PATCH; a wizard draft is replaced whole with PUT
    // (see `UpdateWizardDraftRequestDto` — a partial update of a step tree is
    // what makes a half-applied drag-reorder possible).
    const method = (meta?.method as string | undefined) ?? 'PATCH';
    const response = await rawRequest(`/${resource}/${String(id)}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    });
    return { data: (await readJson(response)) as never };
  },

  async deleteOne({ resource, id }) {
    await rawRequest(`/${resource}/${String(id)}`, { method: 'DELETE' });
    return { data: { id } as never };
  },

  /**
   * Everything that is not a Refine CRUD verb: cursor-paginated list reads, the
   * audited child reveal (a GET with a required `reason`), and the POST actions
   * on users, reports and catalogue products.
   *
   * ⚠️ `meta.unversioned` routes around the `/api/v1` prefix, for the health
   * endpoints only. `HealthController` is deliberately registered outside the
   * version prefix so one path serves the Compose healthcheck, Caddy and the
   * admin Ops screen — putting it through `apiUrl` would ask for
   * `/api/v1/health/queues`, which does not exist.
   */
  async custom({ url, method, payload, query, headers, meta }) {
    const target = `/${url.replace(/^\/+/, '')}${buildQuery(query as Json | undefined)}`;

    const init = {
      method: (method ?? 'get').toUpperCase(),
      headers: payload ? { 'Content-Type': 'application/json', ...headers } : headers,
      body: payload ? JSON.stringify(payload) : undefined,
    };

    const response =
      meta?.['unversioned'] === true
        ? await rawUnversionedRequest(target, init)
        : await rawRequest(target, init);

    return { data: (await readJson(response)) as never };
  },
};

/** The download URL for the vendor-report CSV, which streams rather than JSON. */
export function vendorReportUrl(query: Record<string, unknown>): string {
  return `${apiUrl('/admin/analytics/vendor-report')}${buildQuery(query)}`;
}
